import { useEffect, useRef, useState } from 'react';
import { message } from 'antd';
import { observer } from 'mobx-react-lite';
import { useBlocker, useNavigate } from 'react-router-dom';

import { useResource } from 'app/common/api';
import { AppPage } from 'app/common/app-page/features/app-page/app-page';
import {
  NotificationsProvider,
  useNotifyFactory,
} from 'app/common/notifications/features/notifications';
import { appRoute } from 'app/railways/router/routes';
import { PageContent } from 'app/ui';

import { TReportTemplate } from '../core/report';
import { checkUnsavedChangesNewFile } from '../modules/report-designer/check-unsaved-changes';
import { ReportDesignerContainer } from '../modules/report-designer/report-designer-container';
import { useReportDesignerController } from '../modules/report-designer/use-report-designer-controller';
import { addReportTemplateRequest } from '../requests/add-report-template.request';

type FormFields = {
  name: string;
  file: any;
};

export const AddReportsPage = observer(() => {
  const pageNotify = useNotifyFactory();
  const navigate = useNavigate();
  const messageRef = useRef<CallableFunction | null>(null);
  const [reportSavedTime, setReportSavedTime] = useState<number>(0);

  const reportDesigner = useReportDesignerController({
    onSaveReport(event) {
      event.preventDefault = true;

      messageRef.current = message.loading({
        type: 'loading',
        content: 'Сохраняем шаблон отчета на сервер...',
        getPopupContainer: () => {
          const container = document.createElement('div');
          container.style.position = 'relative';
          container.style.zIndex = 'calc(infinity)';
          document.body.appendChild(container);

          return container;
        },
      });

      const jsonReport = event.report.saveToJsonString();
      const fileBlob = new Blob([jsonReport], {
        type: 'application/json',
      });

      const isFileNameHasExt = event.fileName.slice(-4) === '.mrt';
      const userFileName = isFileNameHasExt
        ? event.fileName.slice(0, event.fileName.lastIndexOf('.'))
        : event.fileName;

      const fileName = userFileName;

      addReportTemplateResource.send({
        file: fileBlob,
        name: fileName,
      });
    },
    onSaveAsReport(event) {
      event.fileName = 'Новый отчет';
    },
  });

  const routeBlocker = useBlocker(({ currentLocation, nextLocation }) => {
    const reportChangedTime =
      reportDesigner.controller.report?.reportChanged.jsDate.getTime() || 0;
    const reportCreatedTime =
      reportDesigner.controller.report?.reportCreated.jsDate.getTime() || 0;

    const hasUnsavedChanges = checkUnsavedChangesNewFile({
      reportChangedTime,
      reportSavedTime,
      reportCreatedTime,
    });

    const isNotReportView = !nextLocation.pathname.includes('/report/');

    const isTransitionBlocked =
      hasUnsavedChanges &&
      isNotReportView &&
      currentLocation.pathname !== nextLocation.pathname;

    return isTransitionBlocked;
  });

  useEffect(() => {
    if (routeBlocker.state === 'blocked') {
      const userResponse = confirm(
        'Последние изменения в шаблоне не были сохранены на сервер. Продолжить переход на другую страницу?',
      );

      if (userResponse) {
        routeBlocker.proceed();
      }

      return () => routeBlocker.reset();
    }
  }, [routeBlocker]);

  const addReportTemplateResource = useResource<FormFields, TReportTemplate>({
    request: addReportTemplateRequest,
    onFail(error) {
      messageRef.current?.();
      message.error({
        // type: 'success',
        content: error.message,
        getPopupContainer: () => {
          const container = document.createElement('div');
          container.style.position = 'relative';
          container.style.zIndex = 'calc(infinity)';
          document.body.appendChild(container);

          return container;
        },
      });
    },
    onDone(result) {
      setReportSavedTime(Date.now());

      const path = appRoute.REPORT.buildPath({ id: result.id });
      messageRef.current?.();

      message.success({
        type: 'success',
        content: 'Шаблон отчета сохранен',
        getPopupContainer: () => {
          const container = document.createElement('div');
          container.style.position = 'relative';
          container.style.zIndex = 'calc(infinity)';
          document.body.appendChild(container);

          return container;
        },
      });

      navigate(path);
    },
  });

  return (
    <NotificationsProvider value={pageNotify}>
      <AppPage perm="PERM_REPORT_TEMPLATE_CREATE" style={{ height: '100%' }}>
        <PageContent style={{ height: '100%' }}>
          <ReportDesignerContainer
            reportDesignerController={reportDesigner.controller}
          />
          {/* <TemplateReportDesigner onSave={handleReportSave} /> */}
        </PageContent>
      </AppPage>
    </NotificationsProvider>
  );
});
